/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react'

import { isEmpty, isNull, isUndefined } from 'lodash'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import { Plus as PlusIcon } from 'react-feather'
import { useForm, Controller, FormContext } from 'react-hook-form'
import {
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  Link,
  useMediaQuery,
  TablePagination,
} from '@material-ui/core'
import { useTheme } from '@material-ui/styles'

import {
  UserCompaniesTable,
  UserCompaniesForm,
  ProfileSection,
} from './components'
import {
  Select as SelectComponent,
  Card,
  Permitted,
  LoadingBox,
  OrganizationUsersTable,
  OrganizationUsersForm,
  BasePhoneInput,
  CompanyTypeAndSeachInput,
  OrganizationTypeAndSearchInput,
  ConfirmationDialog,
} from 'components'

import useSnackbar from 'hooks/useSnackbar'
import usePagination from 'hooks/usePagination'
import { useFetch } from 'hooks'

import * as service from 'service'
import helpers from 'helpers'
import { routes } from 'Routes'
import constants from 'constants/index'

import schema from './schema'
import useStyles from './styles'
import { reverse } from 'named-urls'

const FormUsers = ({ userClient, isEdit, user }) => {
  const { DEFAULT_DDI } = constants.countries

  const createProfilePermissionTag = userClient
    ? constants.permissions.CLIENT_PROFILES.CREATE
    : constants.permissions.COLLABORATOR_PROFILES.CREATE

  const [loading, setLoading] = useState(false)
  const [isLoadingProfile, setIsLoadingProfile] = useState(false)
  const [linkedCompany, setLinkedCompany] = useState(false)
  const [linkedOrganization, setLinkedOrganization] = useState(false)
  const [profile, setProfile] = useState({})
  const [profileTypeResponsible, setProfileTypeResponsible] = useState(false)
  const [profiles, setProfiles] = useState([])
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false)

  const theme = useTheme()

  const { perPage, page, handleChangePage, handleChangeRowsPerPage } =
    usePagination(5)

  const {
    perPage: perPageOrganizationUser,
    page: pageOrganizationUser,
    handleChangePage: handleChangePageOrganizationUser,
    handleChangeRowsPerPage: handleChangeRowsPerPageOrganizationUser,
  } = usePagination(5)

  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'), {
    defaultMatches: true,
  })
  const classes = useStyles()

  const history = useHistory()
  const snackbar = useSnackbar()

  const formMethods = useForm({
    validationSchema: schema(userClient, isEdit, profileTypeResponsible),
    defaultValues: {
      company: null,
      organization: null,
      name: user?.name ?? '',
      email: user?.email ?? '',
      profileId: !userClient ? user?.profile?.id ?? '' : '',
      phone: user?.phone || DEFAULT_DDI,
      linkType: '',
    },
  })

  const { handleSubmit, errors, reset, getValues, watch, control, setValue } =
    formMethods

  const linkType = watch('linkType') || null
  const company = watch('company') || null
  const organization = watch('organization') || null

  const phoneEnableField = profileTypeResponsible || !userClient

  const redirectRollBack = () => {
    userClient
      ? history.push(routes.users.all)
      : history.push(routes.collaborators.all)
  }

  const onSubmit = async (data) => {
    try {
      setLoading(true)
      if (userClient) {
        if (isEdit) {
          await service.backoffice.users.update({
            userId: user.id,
            user: { ...data },
          })

          setConfirmationDialogOpen(true)
        } else {
          const response = await service.backoffice.users.create({
            user: {
              ...data,
              companyId: company?.id,
              organizationId: organization?.id,
              profileId: profile?.id,
            },
          })

          history.push(
            reverse(routes.users.edit, { userId: response?.data?.user?.id }),
          )

          redirectRollBack()
        }
      } else {
        if (isEdit) {
          if (helpers.validates.phone.isOptional(data.phone)) delete data?.phone
          await service.dponet.userCollaborators.edit({
            userId: user.id,
            user: data,
          })
        } else {
          await service.dponet.userCollaborators.create({ user: data })
        }

        redirectRollBack()
      }

      snackbar.open({
        message: `Usuário ${isEdit ? 'editado' : 'criado'} com sucesso`,
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(
          error.response.data.error,
          false,
        ),
        variant: 'error',
      })
      reset(data)
    } finally {
      setLoading(false)
    }
  }

  const navigateToCreateProfile = () => {
    if (userClient) {
      if (linkType === 'organizationUser') {
        history.push(routes.userProfiles.newOrganizations)
      } else {
        history.push(routes.userProfiles.new)
      }
    } else {
      history.push(routes.collaboratorProfiles.new)
    }
  }

  const selectedProfile = () => {
    const profileId = getValues('profileId')

    if (!!profileId && !isEmpty(profiles)) {
      const profile = profiles.find((profile) => profile.id === profileId)

      if (!!profile) {
        return setProfile(profile)
      }
    }

    return setProfile({})
  }

  useEffect(() => {
    setValue('company', null)
    setValue('organization', null)
    setValue('profileId', '')
  }, [linkType])

  useEffect(() => {
    selectedProfile()
  }, [profiles, watch('profileId')])

  const {
    response: userCompaniesResponse,
    isStillLoading: isStillLoadingUserCompanies,
    refresh: refreshUserCompanies,
  } = useFetch(
    service.dponet.userCompanies.get,
    {
      userId: user?.id,
      perPage: perPage,
      page: page,
    },
    [page, perPage],
    !isUndefined(user),
  )

  const {
    response: organizationUsersResponse,
    isStillLoading: isStillLoadingOrganizationUsers,
    refresh: refreshOrganizationUsers,
  } = useFetch(
    service.backoffice.organizationUsers.all,
    {
      userId: user?.id,
    },
    [perPageOrganizationUser, pageOrganizationUser],
    !isUndefined(user),
  )

  useEffect(() => {
    const loadProfiles = async () => {
      if (userClient && isNull(linkType)) return
      if (isNull(company) && isNull(organization) && userClient) return

      setIsLoadingProfile(true)

      const profileRoute = userClient
        ? linkType === 'userCompany'
          ? service.backoffice.userProfiles.get
          : service.backoffice.advisorProfiles.get
        : service.dponet.collaboratorProfiles.get

      const response = await profileRoute({
        perPage: 100000,
        listDefaults: true,
        status: true,
        companyId: company?.id,
        organizationId: organization?.id,
      })

      if (userClient && linkType === 'organizationUser') {
        setProfiles([
          ...response?.data?.profiles,
          ...constants.profile.DEFAULT_DPO_PROFILES_OPTIONS,
        ])
      } else {
        setProfiles(response?.data?.profiles)
      }
      setIsLoadingProfile(false)
    }

    loadProfiles()
  }, [company, organization])

  useEffect(() => {
    if (userCompaniesResponse?.data?.userCompanies) {
      const isResponsible = userCompaniesResponse.data.userCompanies.some(
        (userCompany) => userCompany.responsable === true,
      )
      setProfileTypeResponsible(isResponsible)
    }
  }, [userCompaniesResponse])

  return (
    <FormContext {...formMethods}>
      {loading ? (
        <LoadingBox />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Card
            title={`Preencha os dados do ${
              userClient ? 'usuário' : 'colaborador'
            }`}
          >
            <Grid container spacing={2}>
              <Grid item md={6} xs={12}>
                <Controller
                  as={
                    <TextField
                      label="Nome"
                      type="text"
                      color="primary"
                      variant="outlined"
                      error={!!errors.name}
                      helperText={errors?.name?.message}
                      fullWidth
                    />
                  }
                  control={control}
                  name="name"
                  mode="onBlur"
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <Controller
                  as={
                    <TextField
                      label="Email"
                      type="email"
                      color="primary"
                      variant="outlined"
                      error={!!errors.email}
                      helperText={errors?.email?.message}
                      fullWidth
                    />
                  }
                  control={control}
                  name="email"
                  mode="onBlur"
                />
              </Grid>
              {userClient && !isEdit && (
                <>
                  <Grid item md={6} xs={12}>
                    <Controller
                      as={
                        <SelectComponent
                          items={[
                            { id: 'userCompany', name: 'Empresa' },
                            { id: 'organizationUser', name: 'Grupo Advisor' },
                          ]}
                          label="Tipo de Vínculo"
                          error={!!errors.linkType}
                          helperText={errors?.linkType?.message}
                        />
                      }
                      control={control}
                      name="linkType"
                      mode="onBlur"
                    />
                  </Grid>
                  {linkType && (
                    <>
                      <Grid item md={6} xs={12}>
                        {linkType === 'userCompany' ? (
                          <Controller
                            as={
                              <CompanyTypeAndSeachInput
                                fieldName="company"
                                control={control}
                                errors={errors}
                              />
                            }
                            name="company"
                            control={control}
                            mode="onBlur"
                          />
                        ) : (
                          <Controller
                            as={
                              <OrganizationTypeAndSearchInput fieldName="organization" />
                            }
                            name="organization"
                            control={control}
                            mode="onBlur"
                          />
                        )}
                      </Grid>
                    </>
                  )}
                </>
              )}
              {phoneEnableField && (
                <Grid item md={6} xs={12}>
                  <Controller
                    as={
                      <BasePhoneInput
                        label={
                          userClient
                            ? 'Telefone do usuário'
                            : 'Telefone do colaborador'
                        }
                        type="text"
                        error={!!errors.phone}
                        helperText={errors?.phone?.message}
                        fullWidth
                      />
                    }
                    control={control}
                    name="phone"
                    mode="onChange"
                  />
                </Grid>
              )}
            </Grid>
          </Card>

          {(!isEdit || !userClient) && (
            <Box mt={3}>
              <Typography variant="h4">
                Perfil de usuário e permissão
              </Typography>
              <Box mt={1} mb={2}>
                <Box className={classes.selectProfileBox}>
                  <Controller
                    control={control}
                    name="profileId"
                    mode="onBlur"
                    as={
                      <SelectComponent
                        label={
                          userClient
                            ? 'Perfil de usuário'
                            : 'Perfil de colaborador'
                        }
                        items={profiles || []}
                        error={!!errors.profileId}
                        helperText={errors?.profileId?.message}
                        loading={isLoadingProfile}
                      />
                    }
                  />
                </Box>
                <Permitted tag={createProfilePermissionTag}>
                  <Box pt={1}>
                    <Typography variant="body2" color="textPrimary">
                      Não encontrou a opção? Clique{' '}
                      <Link
                        onClick={navigateToCreateProfile}
                        className={classes.link}
                      >
                        aqui
                      </Link>{' '}
                      para adicionar
                    </Typography>
                  </Box>
                </Permitted>
              </Box>
              {!isEmpty(profile) && (
                <Box>
                  <ProfileSection
                    simplifiedProfile={profile}
                    profileId={profile?.id}
                    notCollaborator={userClient}
                    isAdvisor={linkType === 'organizationUser'}
                  />
                </Box>
              )}
            </Box>
          )}
          {userClient && isEdit && (
            <>
              <Card
                actionButton={
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<PlusIcon />}
                    onClick={() => setLinkedCompany(true)}
                  >
                    Vincular
                  </Button>
                }
                title="Empresas vinculadas"
                dropdown
              >
                <Box>
                  <UserCompaniesTable
                    user={user}
                    refresh={refreshUserCompanies}
                    userCompanies={userCompaniesResponse?.data?.userCompanies}
                    isLoading={isStillLoadingUserCompanies}
                  />
                  <Box px={2} display="flex" justifyContent="flex-end">
                    <TablePagination
                      component="div"
                      count={userCompaniesResponse?.data?.meta?.totalCount || 0}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      page={page - 1}
                      rowsPerPage={perPage}
                      rowsPerPageOptions={[5, 10, 25, 100]}
                      labelRowsPerPage={isDesktop ? 'Por página' : ''}
                      nextIconButtonProps={{ size: 'small' }}
                      backIconButtonProps={{ size: 'small' }}
                    />
                  </Box>
                </Box>
              </Card>
              <Card
                actionButton={
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<PlusIcon />}
                    onClick={() => setLinkedOrganization(true)}
                  >
                    Vincular
                  </Button>
                }
                title="Grupos Advisor vinculados"
                dropdown
              >
                <Box>
                  <OrganizationUsersTable
                    organizationUsers={
                      organizationUsersResponse?.data?.organizationUsers
                    }
                    refresh={refreshOrganizationUsers}
                    showOrganization
                    isOrganizationUsersLoading={isStillLoadingOrganizationUsers}
                  />
                  <Box px={2} display="flex" justifyContent="flex-end">
                    <TablePagination
                      component="div"
                      count={
                        organizationUsersResponse?.data?.meta?.totalCount || 0
                      }
                      onPageChange={handleChangePageOrganizationUser}
                      onRowsPerPageChange={
                        handleChangeRowsPerPageOrganizationUser
                      }
                      page={page - 1}
                      rowsPerPage={perPage}
                      rowsPerPageOptions={[5, 10, 25, 100]}
                      labelRowsPerPage={isDesktop ? 'Por página' : ''}
                      nextIconButtonProps={{ size: 'small' }}
                      backIconButtonProps={{ size: 'small' }}
                    />
                  </Box>
                </Box>
              </Card>
            </>
          )}

          <Box display="flex" justifyContent="flex-end">
            <Box pr={1}>
              <Button
                type="button"
                variant="contained"
                onClick={redirectRollBack}
              >
                Voltar
              </Button>
            </Box>
            <Box>
              <Button color="primary" variant="contained" type="submit">
                Salvar
              </Button>
            </Box>
          </Box>

          {userClient && (
            <UserCompaniesForm
              userId={user?.id}
              setOpen={setLinkedCompany}
              refresh={refreshUserCompanies}
              open={linkedCompany}
            />
          )}

          {userClient && (
            <OrganizationUsersForm
              userId={user?.id}
              setOpen={setLinkedOrganization}
              refresh={refreshOrganizationUsers}
              open={linkedOrganization}
            />
          )}
        </form>
      )}
      <ConfirmationDialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        actionAcceptButton={redirectRollBack}
        message={`Em caso de alteração do e-mail, as instruções para criar uma nova senha foram enviadas para o endereço de e-mail atualizado.`}
        buttonText="Entendido"
        hideCancelButton
      />
    </FormContext>
  )
}

FormUsers.propTypes = {
  userClient: PropTypes.bool,
  isEdit: PropTypes.bool,
  user: PropTypes.object,
  refreshUser: PropTypes.func,
}

FormUsers.defaultProps = {
  userClient: false,
  isEdit: false,
  refreshUser: () => {},
}

export default FormUsers
